import React, { useState, useMemo, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import ViewingToolsHeader from './ViewingToolsHeader';
import PlayByPlaySearchFilter from './PlayByPlaySearchFilter';
import {
  EventsList,
  TeamInfo,
  EnrichedEvent,
  PeriodGroup,
  DEFAULT_TEAM_INFO,
  getEventTimeMs,
  getEventKey,
} from './EventsList';
import { EnrichedEventsProvider } from '../context/EnrichedEventsContext';
import { ClipCreationContent, ClipEventMetadata } from './ClipCreationContent';
import { MediaProvider } from '../context/MediaContext';
import { useSportEvents } from '../context/SportEventContext';
import { SportEvent } from '../types/SportEventTypes';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';
import { useTimestampState } from '@/shared/hooks/websocket/useTimestamp';
import 'simplebar-react/dist/simplebar.min.css';
import { TeamPlayersListWithSearch } from '../../teams/components/TeamPlayersListWithSearch';
import { AutoIsoPlayer } from '../../autoIso/types/AutoIsoModel';
import { PlayerDetailPanel } from '../../players/components/PlayerDetailPanel';
import { PlayerShift } from '../../players/types/PlayerShiftTypes';
import { ShiftClipView } from '../../players/components/ShiftClipView';
import {
  getPeriodNumber,
  isGoalEvent,
  PlayByPlayPanelProps,
  sortPlayersByNumber,
} from './PlayByPlayUtils';
import { useActiveSportEventTypes } from '../sports/hooks';
import { getFilterGroupMembers, getFilterPillOwner } from '../sports/registry';
import { getPeriodChronologicalOrder } from '@/shared/utils/getPeriodName';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import useClipGroupCountsQuery from '@/features/clipGroups/hooks/useClipGroupCountsQuery';
import Tracking from '@/shared/services/tracking';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useUserStateContext } from '@/shared/components/UserStateProvider';
import { Access } from '@/types/user';
import { NEW_EVENT_FLASH_MS } from '../styles/playByPlayStyles';
import { RevisionState } from '@c360/types';

type FindPlayerById = ReturnType<typeof useCurrentGame>['findPlayerById'];
type PlayerInfo = ReturnType<FindPlayerById>;

type RightRailTab = 'pbp' | 'home' | 'away';

// A diff bigger than this is a backlog replay (initial load / reconnect), not
// live play — don't flash or scroll for it.
const MAX_LIVE_EVENT_BATCH = 5;

type RightRailView =
  | { type: 'default' }
  | { type: 'player-detail'; playerId: string; teamSide: 'home' | 'away' }
  | {
      type: 'clip-creation';
      event: SportEvent;
      metadata: ClipEventMetadata;
      autoOpenFirstClip?: boolean;
    }
  | { type: 'shift-clip'; shift: PlayerShift; playerId: string };

const getRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const getFirstNameFromName = (name: unknown): string | null => {
  if (typeof name === 'string') {
    return name.trim().split(/\s+/)[0] || null;
  }

  const nameRecord = getRecord(name);
  if (nameRecord) {
    const firstName = nameRecord.firstname ?? nameRecord.firstName ?? nameRecord.first_name;
    return typeof firstName === 'string' ? firstName.trim() || null : null;
  }

  return null;
};

const getParticipantPlayerIds = (participant: Record<string, unknown>): string[] => {
  const id = getRecord(participant.id);
  if (!id) return [];

  return [id.smt, id.main]
    .filter((playerId): playerId is string | number => {
      return typeof playerId === 'string' || typeof playerId === 'number';
    })
    .map(String);
};

const participantFirstNameMatches = (
  participant: unknown,
  searchLower: string,
  lookupPlayer: (playerId: string) => PlayerInfo,
): boolean => {
  const participantRecord = getRecord(participant);
  if (!participantRecord) return false;

  const player = getRecord(participantRecord.player);

  const firstNames = [
    getFirstNameFromName(participantRecord.name),
    getFirstNameFromName(player?.name),
  ];

  for (const playerId of getParticipantPlayerIds(participantRecord)) {
    const playerInfo = lookupPlayer(playerId);
    firstNames.push(getFirstNameFromName(playerInfo?.displayName));
  }

  return firstNames.some((firstName) => firstName?.toLowerCase().includes(searchLower));
};

const eventPlayerFirstNameMatches = (
  eventData: unknown,
  searchLower: string,
  lookupPlayer: (playerId: string) => PlayerInfo,
): boolean => {
  if (!eventData || typeof eventData !== 'object') return false;

  const visited = new Set<unknown>();

  const visit = (value: unknown): boolean => {
    if (!value || typeof value !== 'object' || visited.has(value)) {
      return false;
    }

    visited.add(value);

    if (participantFirstNameMatches(value, searchLower, lookupPlayer)) {
      return true;
    }

    if (Array.isArray(value)) {
      return value.some(visit);
    }

    return Object.values(value).some(visit);
  };

  return visit(eventData);
};

const usePlayerLookupCache = (findPlayerById: FindPlayerById, cacheKey?: string | number) => {
  const cacheRef = useRef<Map<string, PlayerInfo>>(new Map());

  useEffect(() => {
    cacheRef.current.clear();
  }, [findPlayerById, cacheKey]);

  return useCallback(
    (playerId: string) => {
      const cached = cacheRef.current.get(playerId);
      if (cached) return cached;

      const result = findPlayerById(playerId);
      if (result) {
        cacheRef.current.set(playerId, result);
      }
      return result;
    },
    [findPlayerById],
  );
};

interface PeriodTabsBarProps {
  periods: number[];
  selectedPeriod: number | null;
  parsePeriod: (period: number) => string;
  onPeriodChange: (period: number) => void;
  hideClippedEvents: boolean;
  onToggleHideClipped: () => void;
}

const PeriodTabsBar = React.memo(function PeriodTabsBar({
  periods,
  selectedPeriod,
  parsePeriod,
  onPeriodChange,
  hideClippedEvents,
  onToggleHideClipped,
}: PeriodTabsBarProps) {
  if (periods.length === 0 || selectedPeriod === null) return null;

  const fillColor = hideClippedEvents ? '#27272E' : '#DBDBE1';
  const backgroundColor = hideClippedEvents ? '#DBDBE1' : 'rgba(255, 255, 255, 0.11)';

  return (
    <Box
      sx={{
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        alignItems: 'center',
        pr: 1,
      }}
    >
      <Tabs
        value={selectedPeriod}
        onChange={(_, value) => onPeriodChange(value)}
        variant="fullWidth"
        sx={{
          display: 'flex',
          flexGrow: 1,
          '& .MuiTab-root': {
            color: 'rgba(255,255,255,0.6)',
            fontFamily: 'Sohne',
            fontWeight: 400,
            textTransform: 'none',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
          },
          '& .Mui-selected': {
            color: 'white',
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#123be2',
            height: 3,
          },
        }}
      >
        {periods.map((period) => (
          <Tab key={period} label={`${parsePeriod(period)}`} value={period} />
        ))}
      </Tabs>
      <Tooltip title={hideClippedEvents ? 'Show All Events' : 'Show Events with Clips'}>
        <Box
          sx={{
            display: 'flex',
            cursor: 'pointer',
            width: '48px',
            height: '32px',
            borderRadius: 0.5,
            backgroundColor: backgroundColor,
            alignItems: 'center',
            ml: 1,
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.22)',
            },
          }}
        >
          <IconButton
            size="medium"
            disableRipple
            sx={{
              color: fillColor,
              '&:hover': {
                backgroundColor: 'transparent',
                '& svg path': {
                  fill: fillColor,
                },
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              onToggleHideClipped();
            }}
            title="Create clip"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.83952 3H18.1605C19.1776 3.00383 20.152 3.40687 20.8712 4.12125C21.5904 4.83563 21.9961 5.80344 22 6.81372V17.1863C22.0009 18.196 21.5981 19.1648 20.8802 19.8797L20.8444 19.912C20.1299 20.608 19.1694 20.9985 18.1686 21H5.83952C5.33556 20.9997 4.83662 20.9005 4.37131 20.7083C3.906 20.516 3.48347 20.2344 3.12793 19.8797L3.09538 19.8441C2.39362 19.135 2.00027 18.1806 2 17.1863L2 6.81372C2.00301 5.80318 2.40849 4.83488 3.12789 4.12031C3.84729 3.40575 4.82214 3.00299 5.83952 3ZM10.7891 10.7164L14.8988 13.5666C14.9843 13.6207 15.0551 13.6949 15.1049 13.7827C15.1546 13.8704 15.1819 13.969 15.1841 14.0698C15.1864 14.1705 15.1636 14.2702 15.1178 14.3601C15.072 14.4499 15.0046 14.5272 14.9215 14.5851L10.8249 17.3964C10.7152 17.4873 10.577 17.537 10.4342 17.5371C10.3528 17.5377 10.2721 17.5224 10.1967 17.4919C10.1213 17.4613 10.0527 17.4163 9.9949 17.3594C9.9371 17.3024 9.89122 17.2346 9.85992 17.16C9.82861 17.0853 9.8125 17.0052 9.8125 16.9244V11.2159C9.81097 11.1026 9.84129 10.9911 9.90004 10.8939C9.9588 10.7968 10.0437 10.7178 10.1451 10.6659C10.2466 10.614 10.3606 10.5912 10.4743 10.6002C10.5881 10.6091 10.6971 10.6493 10.7891 10.7164ZM3.19629 7.27932H5.47493L6.94954 4.18825H5.83952C5.13915 4.19038 4.46808 4.46768 3.97284 4.95959C3.47761 5.4515 3.19844 6.11806 3.19629 6.81372V7.27932ZM8.03027 4.18825L6.55729 7.27932H10.6849L12.1611 4.1931L8.03027 4.18825ZM13.2386 4.18825L11.7526 7.27932H15.7728L17.2506 4.18825H13.2386ZM18.3363 4.18825L16.8535 7.27932H20.8037V6.81372C20.8008 6.14732 20.5441 5.5067 20.0851 5.02058C19.6262 4.53445 18.9991 4.23883 18.3298 4.1931L18.3363 4.18825ZM20.8102 8.50153H3.19629V17.1863C3.19899 17.87 3.47059 18.5255 3.95312 19.0131L3.98079 19.039C4.47257 19.5316 5.14111 19.8101 5.83952 19.8134H18.1605C18.8477 19.8128 19.5075 19.546 19.9997 19.0697L20.0257 19.0406C20.2719 18.7979 20.4674 18.5093 20.6009 18.1914C20.7344 17.8734 20.8033 17.5324 20.8037 17.1879V8.50638L20.8102 8.50153Z"
                fill="currentColor"
              />
            </svg>
          </IconButton>
        </Box>
      </Tooltip>
    </Box>
  );
});
PeriodTabsBar.displayName = 'PeriodTabsBar';

interface DebugEventDialogProps {
  debugEvent: SportEvent | null;
  onClose: () => void;
}

const DebugEventDialog = React.memo(function DebugEventDialog({
  debugEvent,
  onClose,
}: DebugEventDialogProps) {
  const handleCopy = useCallback(() => {
    if (!debugEvent) return;
    navigator.clipboard.writeText(JSON.stringify(debugEvent, null, 2));
  }, [debugEvent]);

  return (
    <Dialog
      open={!!debugEvent}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: '#1a1a1a',
            color: 'white',
          },
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        Raw Event Data
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Box
          component="pre"
          sx={{
            bgcolor: '#0d0d0d',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            fontSize: '0.875rem',
            fontFamily: 'monospace',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          {debugEvent ? JSON.stringify(debugEvent, null, 2) : ''}
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', p: 2 }}>
        <Button onClick={handleCopy} sx={{ color: '#2196f3' }}>
          Copy to Clipboard
        </Button>
        <Button onClick={onClose} sx={{ color: 'white' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
});
DebugEventDialog.displayName = 'DebugEventDialog';

// ============================================================================
// Main Component
// ============================================================================

export const PlayByPlayPanel: React.FC<PlayByPlayPanelProps> = ({ width }) => {
  const {
    events,
    isConnected,
    setFilter,
    eventsConnectionState,
    forceReconnect,
  } = useSportEvents();
  const [, setTimestamp] = useTimestampState();
  const {
    parsePeriod,
    game,
    homeTeamLogoPath,
    awayTeamLogoPath,
    homeTeamPrimaryColor,
    awayTeamPrimaryColor,
    findPlayerById,
    fullProcessedTeams,
    gameTypeMetadata,
  } = useCurrentGame();
  const periodNamingMapping = gameTypeMetadata?.periodNamingMapping?.[0];
  const { gameStateManager, trackEntity } = useServerStateContext();
  const { clipGroupCounts } = useClipGroupCountsQuery();
  const { viewOpponentsShifts } = useFlags();
  const { currentOrg } = useUserStateContext();

  const focusTeamId = useMemo(() => {
    const access = (currentOrg as any)?.access as Access[] | undefined;
    if (!access) return undefined;
    const teamEntries = access.filter((a) => a.teamId != null);
    return teamEntries.length === 1 ? teamEntries[0].teamId ?? undefined : undefined;
  }, [currentOrg]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const periodRefsMap = useRef<Map<number, HTMLDivElement>>(new Map());

  const lookupPlayer = usePlayerLookupCache(findPlayerById, game.id);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState<number | null>(null);
  const [debugEvent, setDebugEvent] = useState<SportEvent | null>(null);
  const [selectedEventTypes, setSelectedEventTypes] = useState<Set<string>>(new Set());
  const [selectedHomePlayer, setSelectedHomePlayer] = useState<string>('all');
  const [selectedAwayPlayer, setSelectedAwayPlayer] = useState<string>('all');
  const [hideClippedEvents, setHideClippedEvents] = useState(false);

  // Tab + view state
  const [activeTab, setActiveTab] = useState<RightRailTab>('pbp');
  const [view, setView] = useState<RightRailView>({ type: 'default' });

  // Tab navigation
  const handleTabChange = useCallback(
    (newTab: RightRailTab) => {
      // Re-clicking the active tab isn't a navigation, so don't report one.
      if (newTab !== activeTab) {
        Tracking.getInstance().track('Player Shift', {
          category: 'Clips',
          tab: newTab === 'pbp' ? 'play_by_play' : newTab,
        });
      }
      setActiveTab(newTab);
      setView({ type: 'default' });
    },
    [activeTab],
  );

  // Player detail navigation
  const handlePlayerClick = useCallback((player: AutoIsoPlayer, teamSide: 'home' | 'away') => {
    Tracking.getInstance().track('Select Player Shift', {
      category: 'Clips',
      playerId: String(player.id),
      teamSide,
    });
    setView({ type: 'player-detail', playerId: String(player.id), teamSide });
  }, []);

  const handleBackToRoster = useCallback(() => {
    setView({ type: 'default' });
  }, []);

  // Shift clip navigation
  const handleDownloadShift = useCallback((shift: PlayerShift, playerId: string) => {
    Tracking.getInstance().track('Download Shift', {
      category: 'Clips',
      playerId,
      shiftId: shift.shiftId,
      shiftNumber: shift.shiftNumber,
    });
    setView({ type: 'shift-clip', shift, playerId });
  }, []);

  const handleSeekToShift = useCallback(
    (shift: PlayerShift, playerId: string) => {
      const numericPlayerId = Number(playerId);
      if (Number.isFinite(numericPlayerId)) {
        try {
          trackEntity({ id: numericPlayerId }, 'PlayerShifts');
        } catch (err) {
          console.error('Failed to track player from shift gametime:', err);
        }
      }

      if (setTimestamp && shift.startTimeMs) {
        Tracking.getInstance().track('Seek To Shift', {
          category: 'Clips',
          playerId,
          shiftId: shift.shiftId,
        });
        setTimestamp(new Date(shift.startTimeMs - 5000));
      }
    },
    [trackEntity, setTimestamp],
  );

  const handleCloseShiftClip = useCallback(() => {
    setView((prev) => {
      if (prev.type === 'shift-clip') {
        return {
          type: 'player-detail',
          playerId: prev.playerId,
          teamSide: activeTab as 'home' | 'away',
        };
      }
      return { type: 'default' };
    });
  }, [activeTab]);

  const handleCloseClipDrawer = useCallback(() => {
    setView({ type: 'default' });
  }, []);

  // Team roster data
  const homeTeam = useMemo(
    () => fullProcessedTeams.find((t) => t.id === game?.homeTeam?.id),
    [fullProcessedTeams, game?.homeTeam?.id],
  );
  const awayTeam = useMemo(
    () => fullProcessedTeams.find((t) => t.id === game?.awayTeam?.id),
    [fullProcessedTeams, game?.awayTeam?.id],
  );

  const homeTeamName =game?.homeTeam?.abbreviation;
  const awayTeamName =game?.awayTeam?.abbreviation;


  const homeRosterPlayers = useMemo(() => {
    if (!homeTeam?.players) return [];
    return Object.values(homeTeam.players).flat() as AutoIsoPlayer[];
  }, [homeTeam]);

  const awayRosterPlayers = useMemo(() => {
    if (!awayTeam?.players) return [];
    return Object.values(awayTeam.players).flat() as AutoIsoPlayer[];
  }, [awayTeam]);

  const homeTeamId = game.homeTeam.id;
  const awayTeamId = game.awayTeam.id;

  useEffect(() => {
    if (activeTab === 'pbp') return;
    if (viewOpponentsShifts || focusTeamId === undefined) return;
    if (focusTeamId === homeTeamId && activeTab === 'away') setActiveTab('pbp');
    else if (focusTeamId === awayTeamId && activeTab === 'home') setActiveTab('pbp');
  }, [activeTab, viewOpponentsShifts, focusTeamId, homeTeamId, awayTeamId]);

  const homeTeamInfo = useMemo<TeamInfo>(
    () => ({
      team: 'home',
      logoPath: homeTeamLogoPath || '',
      color: homeTeamPrimaryColor || '#000',
    }),
    [homeTeamLogoPath, homeTeamPrimaryColor],
  );

  const awayTeamInfo = useMemo<TeamInfo>(
    () => ({
      team: 'away',
      logoPath: awayTeamLogoPath || '',
      color: awayTeamPrimaryColor || '#000',
    }),
    [awayTeamLogoPath, awayTeamPrimaryColor],
  );

  const resolveTeamInfo = useCallback(
    (playerInfo?: PlayerInfo | null): TeamInfo => {
      if (!playerInfo?.team?.id) return DEFAULT_TEAM_INFO;
      if (homeTeamId && playerInfo.team.id === homeTeamId) return homeTeamInfo;
      if (awayTeamId && playerInfo.team.id === awayTeamId) return awayTeamInfo;
      return DEFAULT_TEAM_INFO;
    },
    [homeTeamId, awayTeamId, homeTeamInfo, awayTeamInfo],
  );

  const handleSearchChange = useCallback((value: string) => {
    Tracking.getInstance().track('Search Events', {
      category: 'Clips',
      searchQuery: value,
    });
    setSearchQuery(value);
  }, []);

  const handleToggleHideClipped = useCallback(() => {
    Tracking.getInstance().track('Filter Created Clips', {
      category: 'Clips',
      hideClippedEvents: !hideClippedEvents,
    });
    setHideClippedEvents((prev) => !prev);
  }, []);

  const handleCloseDebug = useCallback(() => {
    setDebugEvent(null);
  }, []);

  const handleSeekToEvent = useCallback(
    (event: SportEvent) => {
      if (setTimestamp && event.timestamp) {
        Tracking.getInstance().track('Seek To Event', {
          category: 'Clips',
          eventType: event.payload?.type,
        });
        setTimestamp(new Date(event.timestamp.getTime() - 5000));
      }
    },
    [setTimestamp],
  );

  // Opening an event's clip view. Deliberately untracked: the board pins two
  // separate events on this one handler's call sites — `View Event` on the
  // play-by-play list, `View Shift Event` on the event rows inside a shift
  // card (PlayerDetailPanel). Tracking here emitted both names for a single
  // shift-card click, so each call site names its own event instead.
  const handleCreateClip = useCallback((event: SportEvent, metadata: ClipEventMetadata) => {
    setView({ type: 'clip-creation', event, metadata });
  }, []);

  // The play-by-play list's call site — the board's `View Event` pin.
  const handleViewEvent = useCallback(
    (event: SportEvent, metadata: ClipEventMetadata) => {
      Tracking.getInstance().track('View Event', {
        category: 'Clips',
        eventType: event.payload?.type,
      });
      handleCreateClip(event, metadata);
    },
    [handleCreateClip],
  );

  const handleCreateCustomEvent = useCallback(() => {
    const playHead = gameStateManager?.lastGameState?.head;
    if (!playHead) {
      console.error('No play head available from game state manager');
      return;
    }

    const playHeadInMs = playHead / 1_000_000;
    const timestamp = new Date(playHeadInMs);

    // Build a local-only "draft" event. We deliberately do NOT call the
    // create mutation here — the event is persisted only when the user
    // commits a name (see ClipCreationContent.handleDescriptionBlur).
    // Until then, abandoning the drawer leaves no trace on the backend.
    const draftEvent = {
      id: null,
      timestamp,
      timestampMs: playHead / 1_000_000,
      payload: { type: 'custom' },
      description: '',
    } as unknown as SportEvent;

    setView({
      type: 'clip-creation',
      event: draftEvent,
      metadata: {
        teamLogoPath: '',
        gameTime: '--:--',
        description: '',
      },
    });
  }, [gameStateManager]);

  const handleCustomEventCreated = useCallback((persistedEvent: SportEvent) => {
    const seconds = persistedEvent.clock?.time;
    const gameTimeDisplay =
      typeof seconds === 'number'
        ? `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`
        : '--:--';

    setView({
      type: 'clip-creation',
      event: persistedEvent,
      metadata: {
        teamLogoPath: '',
        gameTime: gameTimeDisplay,
        description: persistedEvent.description ?? '',
      },
      autoOpenFirstClip: true,
    });
  }, []);

  const toggleEventType = useCallback((eventType: string) => {
    setSelectedEventTypes((prev) => {
      const newSet = new Set(prev);
      // A pill may stand in for a whole filter group (e.g. NBA "2-Pointer"
      // covers both basket_made_2pt and basket_missed_2pt). Toggle every
      // member together so the single pill selects/clears the whole group.
      // The pill owner's presence in the set reflects the group's on/off
      // state (see the chip's isSelected check).
      const members = getFilterGroupMembers(eventType);
      const isSelected = newSet.has(eventType);
      for (const member of members) {
        if (isSelected) newSet.delete(member);
        else newSet.add(member);
      }
      Tracking.getInstance().track('Filter PlaybyPlay', {
        category: 'Clips',
        eventTypes: newSet,
      });
      return newSet;
    });
  }, []);

  const handlePeriodChange = useCallback((period: number) => {
    const periodElement = periodRefsMap.current.get(period);
    if (periodElement && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: periodElement.offsetTop,
        behavior: 'smooth',
      });
    }
    setSelectedPeriod(period);
    Tracking.getInstance().track('Jump to Period', {
      category: 'Clips',
      period: period,
    });
  }, []);

  // The list unmounts whenever an overlay view (clip creation, player detail,
  // ...) or another tab replaces it, so remember where it was scrolled and put
  // it back when it remounts — before paint, so there's no jump.
  const savedScrollTopRef = useRef<number | null>(null);
  const isEventsListVisible = view.type === 'default' && activeTab === 'pbp';
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!isEventsListVisible || !container || savedScrollTopRef.current === null) return;
    container.scrollTop = savedScrollTopRef.current;
  }, [isEventsListVisible]);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    savedScrollTopRef.current = container.scrollTop;
    const containerHeight = container.clientHeight;
    const threshold = containerHeight / 3;
    const containerRect = container.getBoundingClientRect();

    let activePeriod: number | null = null;

    periodRefsMap.current.forEach((element, period) => {
      const rect = element.getBoundingClientRect();
      const relativeTop = rect.top - containerRect.top;

      if (relativeTop >= 0 && relativeTop < threshold) {
        activePeriod = period;
      }
    });

    if (activePeriod !== null && activePeriod !== selectedPeriod) {
      setSelectedPeriod(activePeriod);
    }
  }, [selectedPeriod]);

  const { periods, eventTypes } = useMemo(() => {
    const periodSet = new Set<number>();
    const eventTypeSet = new Set<string>();

    for (let i = 0; i < events.length; i++) {
      const event: SportEvent = events[i];

      if (event.clock?.period && !['1', '5', '8'].includes(event.clock.period.toString()[0])) {
        periodSet.add(getPeriodNumber(event.clock.period));
      }

      // Resolve the event's type to the pill that owns its filter group, so a
      // single grouped pill (e.g. NBA "2-Pointer") shows whenever ANY of its
      // members — made or missed — is present in the data.
      const pillOwner = getFilterPillOwner(event.payload?.type);
      if (pillOwner) {
        eventTypeSet.add(pillOwner);
      }
    }

    return {
      periods: Array.from(periodSet).sort(
        (a, b) => getPeriodChronologicalOrder(a) - getPeriodChronologicalOrder(b),
      ),
      eventTypes: Array.from(eventTypeSet).sort(),
    };
  }, [events]);

  const { homeTeamPlayers, awayTeamPlayers } = useMemo(() => {
    const entityIdSet = new Set<string>();
    const hasEventTypeFilter = selectedEventTypes.size > 0;

    for (let i = 0; i < events.length; i++) {
      const event = events[i] as SportEvent;
      //@ts-expect-error
      const { type: eventType, data: eventData } = event.payload;

      if (hasEventTypeFilter && (!eventType || !selectedEventTypes.has(eventType))) {
        continue;
      }

      if (!eventData) continue;

      switch (eventType) {
        case 'pass':
          if (eventData.sender?.id?.smt) entityIdSet.add(eventData.sender.id.smt);
          if (eventData.receiver?.id?.smt) entityIdSet.add(eventData.receiver.id.smt);
          break;
        case 'shot':
        case 'shot_on_goal':
        case 'blocked_shot':
        case 'missed_shot':
          if (eventData.shooter?.id?.smt) entityIdSet.add(eventData.shooter.id.smt);
          break;
        case 'shootout_goal':
        case 'goal':
          if (eventData.shooter?.id?.smt) entityIdSet.add(eventData.shooter.id.smt);
          if (eventData.assister?.id?.smt) entityIdSet.add(eventData.assister.id.smt);
          break;
        case 'giveaway':
          if (eventData.caused?.id?.smt) entityIdSet.add(eventData.caused.id.smt);
          if (eventData.committed?.id?.smt) entityIdSet.add(eventData.committed.id.smt);
          break;
        case 'penalty':
          if (eventData.committed?.id?.smt) entityIdSet.add(eventData.committed.id.smt);
          break;
        case 'goalie_save':
          if (eventData.goalie?.id?.smt) entityIdSet.add(eventData.goalie.id.smt);
          break;
        case 'hit':
          if (eventData.hitter?.id?.smt) entityIdSet.add(eventData.hitter.id.smt);
          if (eventData.was_hit?.id?.smt) entityIdSet.add(eventData.was_hit.id.smt);
          break;
      }
    }

    const homePlayers: Array<{ id: string; name: string; number?: number | string }> = [];
    const awayPlayers: Array<{ id: string; name: string; number?: number | string }> = [];

    entityIdSet.forEach((entityId) => {
      const playerInfo = lookupPlayer(entityId);
      if (!playerInfo?.team?.id) return;

      const playerData = {
        id: entityId,
        name: playerInfo.displayName,
        number: playerInfo.playerNumber,
      };

      if (playerInfo.team.id === homeTeamId) {
        homePlayers.push(playerData);
      } else if (playerInfo.team.id === awayTeamId) {
        awayPlayers.push(playerData);
      }
    });

    homePlayers.sort(sortPlayersByNumber);
    awayPlayers.sort(sortPlayersByNumber);

    return {
      homeTeamPlayers: homePlayers,
      awayTeamPlayers: awayPlayers,
    };
  }, [events, selectedEventTypes, lookupPlayer, homeTeamId, awayTeamId]);

  // Every SES flush hands over a new `events` array, but events the flush
  // didn't touch keep their object identity. Reuse their enriched copy from
  // the previous pass so EventsList's memoized rows skip re-rendering; any
  // change to the other enrichment inputs rebuilds every copy.
  const previousEnrichmentRef = useRef<{
    inputs: unknown[];
    byEvent: Map<SportEvent, EnrichedEvent>;
  } | null>(null);

  const enrichedEvents = useMemo(() => {
    const result: EnrichedEvent[] = [];
    const inputs = [lookupPlayer, clipGroupCounts, homeTeamInfo, awayTeamInfo, resolveTeamInfo];
    const previous = previousEnrichmentRef.current;
    const reusable =
      previous && previous.inputs.every((input, i) => input === inputs[i]) ? previous.byEvent : null;
    const byEvent = new Map<SportEvent, EnrichedEvent>();

    const getClipGroupCount = (eventType: string | undefined, eventId: unknown) => {
      const eventIdStr = String(eventId);
      if (eventType === 'custom') {
        return clipGroupCounts.custom[eventIdStr] || 0;
      }
      return clipGroupCounts.ses[eventIdStr] || 0;
    };

    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      //@ts-expect-error
      const { type: eventType, data: eventData } = event.payload || {};

      const isShootout = event.clock?.period === 700;
      const hasClock = event.clock?.time !== undefined && event.clock?.time !== null && !isNaN(Number(event.clock.time));
      if (!isShootout && !hasClock && eventType !== 'custom') {
        continue;
      }

      const reused = reusable?.get(event);
      if (reused) {
        result.push(reused);
        byEvent.set(event, reused);
        continue;
      }

      const clipGroupCount = getClipGroupCount(eventType, event.id);

      if (!eventData) {
        const enriched = { ...event, clipGroupCount, _entityIds: [] } as EnrichedEvent;
        result.push(enriched);
        byEvent.set(event, enriched);
        continue;
      }

      const payload = event.payload;
      const entityIds: string[] = [];
      const enrichedPayload: any = { ...(payload as any), data: { ...eventData } };
      let primaryEntityId: string | null = null;
      let teamInfo: TeamInfo = DEFAULT_TEAM_INFO;

      switch (eventType) {
        case 'pass':
          if (eventData.sender?.id?.smt) {
            entityIds.push(eventData.sender.id.smt);
            primaryEntityId = eventData.sender.id.smt;
            const senderInfo = lookupPlayer(eventData.sender.id.smt);
            if (senderInfo) {
              enrichedPayload.data.sender = {
                ...eventData.sender,
                player: {
                  id: eventData.sender.id,
                  name: eventData.sender.name,
                  jersey: senderInfo.playerNumber,
                  team_id: senderInfo.team?.id,
                  team: senderInfo.team,
                },
              };
            }
          }
          if (eventData.receiver?.id?.smt) {
            entityIds.push(eventData.receiver.id.smt);
            const receiverInfo = lookupPlayer(eventData.receiver.id.smt);
            if (receiverInfo) {
              enrichedPayload.data.receiver = {
                ...eventData.receiver,
                player: {
                  id: eventData.receiver.id,
                  name: eventData.receiver.name,
                  jersey: receiverInfo.playerNumber,
                  team_id: receiverInfo.team?.id,
                  team: receiverInfo.team,
                },
              };
            }
          }
          break;
        case 'shot':
        case 'missed_shot':
        case 'blocked_shot':
        case 'shot_on_goal':
          if (eventData.shooter?.id?.smt) {
            entityIds.push(eventData.shooter.id.smt);
            primaryEntityId = eventData.shooter.id.smt;
            const shooterInfo = lookupPlayer(eventData.shooter.id.smt);
            if (shooterInfo) {
              enrichedPayload.data.shooter = {
                ...eventData.shooter,
                player: {
                  id: eventData.shooter.id,
                  name: eventData.shooter.name,
                  jersey: shooterInfo.playerNumber,
                  team_id: shooterInfo.team?.id,
                  team: shooterInfo.team,
                },
              };
            }
          }
          break;
        case 'powerplay_start':
          if (eventData.advantage === 'home') {
            teamInfo = homeTeamInfo;
          } else if (eventData.advantage === 'away') {
            teamInfo = awayTeamInfo;
          }
          break;
        case 'shootout_goal':
        case 'goal':
          if (eventData.shooter?.id?.smt) {
            entityIds.push(eventData.shooter.id.smt);
            primaryEntityId = eventData.shooter.id.smt;
            const scorerInfo = lookupPlayer(eventData.shooter.id.smt);
            if (scorerInfo) {
              enrichedPayload.data.shooter = {
                ...eventData.shooter,
                player: {
                  id: eventData.shooter.id,
                  name: {
                    firstname: scorerInfo.displayName.split(' ')[0] || '',
                    lastname: scorerInfo.displayName.split(' ').slice(1).join(' ') || '',
                  },
                  jersey: scorerInfo.playerNumber,
                  team_id: scorerInfo.team?.id,
                  team: scorerInfo.team,
                },
              };
            }
          }
          if (eventData.assister?.id?.smt) {
            entityIds.push(eventData.assister.id.smt);
            const assisterInfo = lookupPlayer(eventData.assister.id.smt);
            if (assisterInfo) {
              enrichedPayload.data.assister = {
                ...eventData.assister,
                player: {
                  id: eventData.assister.id,
                  name: eventData.assister.name,
                  jersey: assisterInfo.playerNumber,
                  team_id: assisterInfo.team?.id,
                  team: assisterInfo.team,
                },
              };
            }
          }
          break;

        case 'giveaway':
          if (eventData.caused?.id?.smt) {
            entityIds.push(eventData.caused.id.smt);
            primaryEntityId = eventData.caused.id.smt;
            const causedInfo = lookupPlayer(eventData.caused.id.smt);
            if (causedInfo) {
              enrichedPayload.data.caused = {
                id: eventData.caused.id,
                name: eventData.caused.name,
                jersey: causedInfo.playerNumber,
                team_id: causedInfo.team?.id,
                team: causedInfo.team,
                position: eventData.caused.position,
              };
            }
          }
          if (eventData.committed?.id?.smt) {
            entityIds.push(eventData.committed.id.smt);
            const committedInfo = lookupPlayer(eventData.committed.id.smt);
            if (committedInfo) {
              enrichedPayload.data.committed = {
                id: eventData.committed.id,
                name: eventData.committed.name,
                jersey: committedInfo.playerNumber,
                team_id: committedInfo.team?.id,
                team: committedInfo.team,
                position: eventData.committed.position,
              };
            }
          }
          break;

        case 'penalty':
          if (eventData.committed?.id?.smt) {
            entityIds.push(eventData.committed.id.smt);
            primaryEntityId = eventData.committed.id.smt;
            const committedInfo = lookupPlayer(eventData.committed.id.smt);
            if (committedInfo) {
              enrichedPayload.data.committed = {
                id: eventData.committed.id,
                name: eventData.committed.name,
                jersey: committedInfo.playerNumber,
                team_id: committedInfo.team?.id,
                team: committedInfo.team,
                position: eventData.committed.position,
              };
            }
          }
          break;

        case 'goalie_save':
          if (eventData.goalie?.id?.smt) {
            entityIds.push(eventData.goalie.id.smt);
            primaryEntityId = eventData.goalie.id.smt;
            const goalieInfo = lookupPlayer(eventData.goalie.id.smt);
            if (goalieInfo) {
              enrichedPayload.data.goalie = {
                id: eventData.goalie.id,
                name: eventData.goalie.name,
                jersey: goalieInfo.playerNumber,
                team_id: goalieInfo.team?.id,
                team: goalieInfo.team,
                position: eventData.goalie.position,
              };
            }
          }
          break;

        case 'faceoff':
          if (eventData.possession?.id?.smt) {
            entityIds.push(eventData.possession.id.smt);
            primaryEntityId = eventData.possession.id.smt;
            const possessionInfo = lookupPlayer(eventData.possession.id.smt);
            if (possessionInfo) {
              enrichedPayload.data.possession = {
                id: eventData.possession.id,
                name: eventData.possession.name,
                jersey: possessionInfo.playerNumber,
                team_id: possessionInfo.team?.id,
                team: possessionInfo.team,
                position: eventData.possession.position,
              };
            }
          }
          break;

        case 'hit':
          if (eventData.hitter?.id?.smt) {
            entityIds.push(eventData.hitter.id.smt);
            primaryEntityId = eventData.hitter.id.smt;
            const hitterInfo = lookupPlayer(eventData.hitter.id.smt);
            if (hitterInfo) {
              enrichedPayload.data.hitter = {
                id: eventData.hitter.id,
                name: eventData.hitter.name,
                jersey: hitterInfo.playerNumber,
                team_id: hitterInfo.team?.id,
                team: hitterInfo.team,
                position: eventData.hitter.position,
              };
            }
          }
          if (eventData.was_hit?.id?.smt) {
            entityIds.push(eventData.was_hit.id.smt);
            const wasHitInfo = lookupPlayer(eventData.was_hit.id.smt);
            if (wasHitInfo) {
              enrichedPayload.data.was_hit = {
                id: eventData.was_hit.id,
                name: eventData.was_hit.name,
                jersey: wasHitInfo.playerNumber,
                team_id: wasHitInfo.team?.id,
                team: wasHitInfo.team,
                position: eventData.was_hit.position,
              };
            }
          }
          break;

        // ── NBA event types ───────────────────────────────────────────
        // Basketball events expose team affiliation directly via top-level
        // `scorer` (baskets / free throws) or `committed_by` (fouls /
        // turnovers / violations), both 'HOME' | 'AWAY'. Substitutions and
        // rebounds carry the side on the player block instead. We drive
        // teamInfo off whichever signal is present and don't touch
        // primaryEntityId — basketball player IDs use `id.main` (NBA stats
        // ID) rather than `.smt`, so they won't resolve through the
        // hockey-keyed lookupPlayer cache today. Player-level enrichment
        // is a follow-up; team-color rendering does not need it.
        case 'basket_made_2pt':
        case 'basket_made_3pt':
        case 'basket_missed_2pt':
        case 'basket_missed_3pt':
        case 'free_throw_made':
        case 'free_throw_missed': {
          const side = eventData.scorer;
          if (side === 'HOME') teamInfo = homeTeamInfo;
          else if (side === 'AWAY') teamInfo = awayTeamInfo;
          break;
        }
        case 'foul_personal':
        case 'foul_offensive':
        case 'foul_drawn':
        case 'turnover':
        case 'violation': {
          const side = eventData.committed_by;
          if (side === 'HOME') teamInfo = homeTeamInfo;
          else if (side === 'AWAY') teamInfo = awayTeamInfo;
          break;
        }
        case 'rebound_offensive':
        case 'rebound_defensive': {
          // Team-attributed rebounds carry no committed_by; fall back to
          // the player block's lowercase `team` field.
          const playerSide = eventData.player?.team;
          if (playerSide === 'home') teamInfo = homeTeamInfo;
          else if (playerSide === 'away') teamInfo = awayTeamInfo;
          break;
        }
      }

      if (primaryEntityId) {
        teamInfo = resolveTeamInfo(lookupPlayer(primaryEntityId));
      }

      const enriched: EnrichedEvent = {
        ...event,
        payload: enrichedPayload,
        _teamInfo: teamInfo,
        clipGroupCount,
        _entityIds: entityIds,
      };
      result.push(enriched);
      byEvent.set(event, enriched);
    }
    previousEnrichmentRef.current = { inputs, byEvent };

    result.sort((a, b) => {
      const timeA = a.time ? (typeof a.time === 'number' ? a.time : Number(a.time)) : 0;
      const timeB = b.time ? (typeof b.time === 'number' ? b.time : Number(b.time)) : 0;
      return timeA - timeB;
    });

    return result;
  }, [events, lookupPlayer, clipGroupCounts, homeTeamInfo, awayTeamInfo, resolveTeamInfo]);

  const enrichedAndFilteredEvents = useMemo(() => {
    const searchLower = searchQuery.trim().toLowerCase();
    const hasSearchQuery = searchLower.length > 0;
    const hasEventTypeFilter = selectedEventTypes.size > 0;
    const hasPlayerFilter = selectedHomePlayer !== 'all' || selectedAwayPlayer !== 'all';
    const shouldHideClipped = hideClippedEvents;

    return enrichedEvents.filter((event) => {
      //@ts-expect-error
      const { type: eventType, data: eventData } = event.payload || {};

      // Shift and substitution events are surfaced only in the player detail
      // view (team tabs), not the PBP list. Substitutions are folded into
      // derived shifts, so they must not also render as standalone
      // "subbed in / subbed out" rows here.
      if (eventType === 'shift' || eventType === 'substitution') return false;

      const descriptionMatchesSearch =
        !!event.description && event.description.toLowerCase().includes(searchLower);
      const playerFirstNameMatchesSearch =
        hasSearchQuery && eventPlayerFirstNameMatches(eventData, searchLower, lookupPlayer);

      if (hasSearchQuery && !descriptionMatchesSearch && !playerFirstNameMatchesSearch) {
        return false;
      }
      if (hasEventTypeFilter && (!eventType || !selectedEventTypes.has(eventType))) {
        return false;
      }
      if (shouldHideClipped && (event.clipGroupCount ?? 0) === 0) {
        return false;
      }
      if (hasPlayerFilter) {
        const entityIds = event._entityIds ?? [];
        if (entityIds.length === 0) return false;
        const homePlayerMatch = selectedHomePlayer === 'all' || entityIds.includes(selectedHomePlayer);
        const awayPlayerMatch = selectedAwayPlayer === 'all' || entityIds.includes(selectedAwayPlayer);
        if (!homePlayerMatch || !awayPlayerMatch) return false;
      }

      return true;
    });
  }, [
    enrichedEvents,
    searchQuery,
    selectedEventTypes,
    selectedHomePlayer,
    selectedAwayPlayer,
    hideClippedEvents,
    lookupPlayer,
  ]);

  const eventsByPeriod = useMemo(() => {
    // enrichedAndFilteredEvents is already sorted ascending by `event.time`
    // (see the result.sort in enrichedEvents), and that wall-clock value is the
    // SAME millisecond unit on both SES events (transformSESEvent: nanos->ms)
    // and custom events (transformToSportEvent: Number(timestampMs)). So an
    // event with no game clock — e.g. a custom event created for a game that
    // has no time-mapping, so the backend couldn't resolve `clock` — is already
    // sitting in its correct chronological slot. It just lacks a `period` to
    // group under, which otherwise dumps it into a phantom "period 0" pinned to
    // the very top of the list, detached from the quarter it belongs to.
    //
    // Resolve a clock-less event to the period of its nearest neighbor in time:
    // a forward pass inherits the most recent known period, and a backward pass
    // covers events that precede every clocked event (e.g. a pregame custom
    // event) by inheriting the nearest later period. Events that DO carry a
    // clock keep their own period unchanged, so this is a pure fallback.
    const resolvedPeriods = new Array<number>(enrichedAndFilteredEvents.length);

    let lastKnownPeriod = 0;
    for (let i = 0; i < enrichedAndFilteredEvents.length; i++) {
      const period = enrichedAndFilteredEvents[i].clock?.period;
      if (period) lastKnownPeriod = getPeriodNumber(period);
      resolvedPeriods[i] = lastKnownPeriod;
    }

    let nextKnownPeriod = 0;
    for (let i = enrichedAndFilteredEvents.length - 1; i >= 0; i--) {
      const period = enrichedAndFilteredEvents[i].clock?.period;
      if (period) {
        nextKnownPeriod = getPeriodNumber(period);
      } else if (resolvedPeriods[i] === 0 && nextKnownPeriod !== 0) {
        resolvedPeriods[i] = nextKnownPeriod;
      }
    }

    const periodMap = new Map<number, SportEvent[]>();

    enrichedAndFilteredEvents.forEach((event, i) => {
      const periodNum = resolvedPeriods[i];
      if (!periodMap.has(periodNum)) {
        periodMap.set(periodNum, []);
      }
      periodMap.get(periodNum)!.push(event);
    });

    return Array.from(periodMap.entries()).sort(
      (a, b) => getPeriodChronologicalOrder(a[0]) - getPeriodChronologicalOrder(b[0]),
    );
  }, [enrichedAndFilteredEvents]);

  const groupedEventsByPeriod = useMemo<PeriodGroup[]>(() => {
    return eventsByPeriod.map(([period, events]) => {
      const groups: Array<{ type: 'events' | 'goal'; events: SportEvent[] }> = [];
      let currentGroup: SportEvent[] = [];

      events.forEach((event) => {
        const isGoal = isGoalEvent(event);

        if (isGoal) {
          if (currentGroup.length > 0) {
            groups.push({ type: 'events', events: currentGroup });
            currentGroup = [];
          }
          groups.push({ type: 'goal', events: [event] });
        } else {
          currentGroup.push(event);
        }
      });

      if (currentGroup.length > 0) {
        groups.push({ type: 'events', events: currentGroup });
      }

      return { period, groups };
    });
  }, [eventsByPeriod]);

  useEffect(() => {
    if (selectedEventTypes.size > 0) {
      // `eventTypes` holds pill owners present in the data. Expand to every
      // group member so pill-less members (e.g. basket_missed_2pt, selected
      // via the grouped "2-Pointer" pill) aren't pruned from the selection.
      const validEventTypes = new Set(eventTypes.flatMap((type) => getFilterGroupMembers(type)));
      const newSelectedEventTypes = new Set(
        Array.from(selectedEventTypes).filter((type) => validEventTypes.has(type)),
      );

      if (newSelectedEventTypes.size !== selectedEventTypes.size) {
        setSelectedEventTypes(newSelectedEventTypes);
      }
    }
  }, [eventTypes, selectedEventTypes]);

  useEffect(() => {
    if (selectedHomePlayer !== 'all' && !homeTeamPlayers.find((p) => p.id === selectedHomePlayer)) {
      setSelectedHomePlayer('all');
    }
  }, [homeTeamPlayers, selectedHomePlayer]);

  useEffect(() => {
    if (selectedAwayPlayer !== 'all' && !awayTeamPlayers.find((p) => p.id === selectedAwayPlayer)) {
      setSelectedAwayPlayer('all');
    }
  }, [awayTeamPlayers, selectedAwayPlayer]);

  useEffect(() => {
    if (periods.length > 0 && selectedPeriod === null) {
      setSelectedPeriod(periods[0]);
    }
  }, [periods, selectedPeriod]);

  // Scope the backend fetch to the active sport's event types. The hook
  // resolves the sport from the current game's league name and falls back
  // to NHL when no league is set (legacy behavior).
  const { backendEventTypes } = useActiveSportEventTypes();
  useEffect(() => {
    setFilter({
      eventTypes: backendEventTypes,
    });
  }, [setFilter, backendEventTypes]);

  // Detect events that arrive live so EventsList can highlight + follow them.
  // The first non-empty set is the baseline (history), never highlighted.
  const seenEventIdsRef = useRef<Set<string> | null>(null);
  const [newEventIds, setNewEventIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    seenEventIdsRef.current = null;
  }, [game.id]);

  useEffect(() => {
    const seen = seenEventIdsRef.current;
    if (!seen) {
      if (enrichedEvents.length > 0) seenEventIdsRef.current = new Set(enrichedEvents.map(getEventKey));
      return;
    }
    const fresh = enrichedEvents.filter((event) => !seen.has(getEventKey(event)));
    fresh.forEach((event) => seen.add(getEventKey(event)));
    if (fresh.length === 0 || fresh.length > MAX_LIVE_EVENT_BATCH) return;

    // Only a genuine INSERT alerts — revisions (UPDATE/DELETE) never do.
    const inserted = fresh.filter((event) => event.revision?.state === RevisionState.INSERT);
    if (inserted.length > 0) setNewEventIds(new Set(inserted.map(getEventKey)));
  }, [enrichedEvents]);

  useEffect(() => {
    if (newEventIds.size === 0) return;
    const timer = setTimeout(() => setNewEventIds(new Set()), NEW_EVENT_FLASH_MS);
    return () => clearTimeout(timer);
  }, [newEventIds]);

  // SES can deliver an event before the video containing it exists — at the
  // live edge the feed runs ahead of the stream. Track the content end (`live`,
  // the live edge with delay applied) against event times; rows past it render
  // dimmed and inert. State holds the time of the last available event (not
  // the raw edge), so the list only re-renders when the edge crosses an event.
  const liveEdgeMsRef = useRef<number | null>(null);
  const [revealedUntilMs, setRevealedUntilMs] = useState(Infinity);

  const updateRevealedUntil = useCallback(() => {
    const liveEdge = liveEdgeMsRef.current;
    // No live edge yet (not connected): don't dim anything.
    if (liveEdge === null) {
      setRevealedUntilMs(Infinity);
      return;
    }
    let revealed = -Infinity;
    // enrichedEvents is sorted ascending by time.
    for (const event of enrichedEvents) {
      const time = getEventTimeMs(event);
      if (time > liveEdge) break;
      revealed = time;
    }
    setRevealedUntilMs(revealed);
  }, [enrichedEvents]);

  useEffect(() => {
    if (!gameStateManager) return;
    const live = gameStateManager.lastGameState?.live;
    liveEdgeMsRef.current = live != null ? live / 1_000_000 : null;
    updateRevealedUntil();

    const handleLiveChange = (nextLive: number) => {
      liveEdgeMsRef.current = nextLive / 1_000_000;
      updateRevealedUntil();
    };
    gameStateManager.on('liveChange', handleLiveChange);
    return () => {
      gameStateManager.off('liveChange', handleLiveChange);
    };
  }, [gameStateManager, updateRevealedUntil]);

  const hasEvents = enrichedAndFilteredEvents.length > 0;

  return (
    <EnrichedEventsProvider value={enrichedEvents}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        width: width ? `${width}px` : '100%',
        backgroundColor: '#1F1F25',
        borderRadius: 1,
      }}
    >
      {/* Overlay views — take full height, hide everything else */}
      {view.type === 'clip-creation' && (
        <MediaProvider key={view.event.id ?? 'draft-custom-event'}>
          <ClipCreationContent
            event={view.event}
            gameId={game?.id?.toString() || ''}
            metadata={view.metadata}
            onClose={handleCloseClipDrawer}
            onEventUpdated={handleCloseClipDrawer}
            onEventCreated={handleCustomEventCreated}
            autoOpenFirstClip={view.autoOpenFirstClip}
          />
        </MediaProvider>
      )}

      {view.type === 'shift-clip' && (
        <ShiftClipView
          shift={view.shift}
          playerId={view.playerId}
          gameId={game?.id?.toString() || ''}
          onClose={handleCloseShiftClip}
          onCreateClip={handleCreateClip}
        />
      )}

      {view.type === 'player-detail' && (
        <PlayerDetailPanel
          playerId={view.playerId}
          teamSide={view.teamSide}
          onBack={handleBackToRoster}
          onCreateClip={handleCreateClip}
          onDownloadShift={handleDownloadShift}
          onSeekToShift={handleSeekToShift}
        />
      )}

      {/* Default view: header (with tabs) + tab content */}
      {view.type === 'default' && (
        <>
          <ViewingToolsHeader
            activeTab={activeTab}
            onTabChange={handleTabChange}
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            homeTeamId={homeTeamId}
            awayTeamId={awayTeamId}
            focusTeamId={focusTeamId}
            onCreateCustomEvent={handleCreateCustomEvent}
            isCelebration={game.isCelebration ?? false}
          />

          {activeTab === 'pbp' && (
            <>
              <PlayByPlaySearchFilter
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                eventTypes={eventTypes}
                selectedEventTypes={selectedEventTypes}
                onToggleEventType={toggleEventType}
              />
              <PeriodTabsBar
                periods={periods}
                selectedPeriod={selectedPeriod}
                parsePeriod={parsePeriod}
                onPeriodChange={handlePeriodChange}
                hideClippedEvents={hideClippedEvents}
                onToggleHideClipped={handleToggleHideClipped}
              />
              <EventsList
                groupedEventsByPeriod={groupedEventsByPeriod}
                hasEvents={hasEvents}
                isConnected={isConnected}
                gameStatus={game.status}
                homeTeamLogoPath={homeTeamLogoPath || ''}
                awayTeamLogoPath={awayTeamLogoPath || ''}
                homeTeamPrimaryColor={homeTeamPrimaryColor || '#000'}
                awayTeamPrimaryColor={awayTeamPrimaryColor || '#000'}
                onDebug={setDebugEvent}
                onSeekToEvent={handleSeekToEvent}
                onClipCreate={handleViewEvent}
                scrollContainerRef={scrollContainerRef}
                periodRefsMap={periodRefsMap}
                onScroll={handleScroll}
                newEventIds={newEventIds}
                revealedUntilMs={revealedUntilMs}
                connectionState={eventsConnectionState}
                onForceReconnect={forceReconnect}
                seasonType={game?.season?.seasonType}
                periodNamingMapping={periodNamingMapping}
              />
            </>
          )}

          {activeTab === 'home' && homeTeam && (
            <TeamPlayersListWithSearch
              team={homeTeam as any}
              players={homeRosterPlayers}
              onPlayerClick={(player) => handlePlayerClick(player, 'home')}
              disableNameEllipsis
            />
          )}

          {activeTab === 'away' && awayTeam && (
            <TeamPlayersListWithSearch
              team={awayTeam as any}
              players={awayRosterPlayers}
              onPlayerClick={(player) => handlePlayerClick(player, 'away')}
              disableNameEllipsis
            />
          )}
        </>
      )}

      <DebugEventDialog debugEvent={debugEvent} onClose={handleCloseDebug} />
    </Box>
    </EnrichedEventsProvider>
  );
};

export default PlayByPlayPanel;
